import { spawn, type ChildProcess } from 'node:child_process';
import { createHash } from 'node:crypto';
import { forComponent } from './logging';
import { IS_WIN, killTree, resolveSpawn } from './platform';
import { mcpToolsToAgentTools, makeMcpToolSearchFromMounted, makeLazyMcpToolSearch, type McpToolSpec, type McpToolSearchOptions } from './mcp';
import type { AgentTool } from './tools';

const log = forComponent('mcp');

/**
 * Real MCP (Model Context Protocol) client — node-only, deliberately kept OUT of the edge-safe
 * `src/index.ts` entry. It speaks JSON-RPC 2.0 over a transport (stdio child process or HTTP),
 * performs the `initialize` handshake + `tools/list` discovery, then hands `(specs, callTool)` to
 * the pure `src/mcp.ts` adapter — so the browser/edge build keeps working without importing this.
 */

const PROTOCOL_VERSION = '2025-06-18';
const DEFAULT_TIMEOUT_MS = 30_000;
/** Hard cap for `tools/call`. NOT the 30s protocol timeout: a tool call is real work (an image render, a crawl)
 *  that legitimately runs for minutes — the harness hands it to a job past its soft timeout and judges it by
 *  progress/idle liveness (src/tools.jobs.ts). This only bounds a call nothing else ever stops. */
export const DEFAULT_CALL_TIMEOUT_MS = 30 * 60_000;

/** An MCP `notifications/progress` payload. */
export interface McpProgress { progress?: number; total?: number; message?: string }

/** Per-request options: cancellation, progress (sends a `_meta.progressToken`), and a deadline override. */
export interface McpRequestOptions {
  signal?: AbortSignal;
  onProgress?: (p: McpProgress) => void;
  timeoutMs?: number;
}

/** The deadline a request gets: the long call cap for `tools/call`, the protocol timeout for everything else. */
function deadlineFor(method: string, spec: { timeoutMs?: number; callTimeoutMs?: number }, opts?: McpRequestOptions): number {
  if (opts?.timeoutMs != null) return opts.timeoutMs;
  if (method === 'tools/call') return spec.callTimeoutMs ?? Math.max(DEFAULT_CALL_TIMEOUT_MS, spec.timeoutMs ?? 0);
  return spec.timeoutMs ?? DEFAULT_TIMEOUT_MS;
}

/** Attach a progress token (MCP `_meta.progressToken`) to request params. */
function withProgressToken(params: unknown, token: string | number): unknown {
  const p = (params && typeof params === 'object' ? params : {}) as Record<string, any>;
  return { ...p, _meta: { ...(p._meta ?? {}), progressToken: token } };
}

const abortError = (signal: AbortSignal): Error => (signal.reason instanceof Error ? signal.reason : Object.assign(new Error('MCP request cancelled'), { name: 'AbortError' }));

/** A JSON-RPC 2.0 transport: request (awaits a result), notify (fire-and-forget), close. */
export interface McpTransport {
  start(): Promise<void>;
  request(method: string, params?: unknown, opts?: McpRequestOptions): Promise<any>;
  notify(method: string, params?: unknown): Promise<void>;
  close(): Promise<void>;
}

// ---------------------------------------------------------------------------
// stdio transport — newline-delimited JSON-RPC over a spawned child's stdin/stdout.
// ---------------------------------------------------------------------------
export interface StdioServerSpec {
  command: string;
  args?: string[];
  env?: Record<string, string>;
  cwd?: string;
  /** Protocol request deadline (initialize, tools/list, …). Default 30s. */
  timeoutMs?: number;
  /** `tools/call` hard cap. Default max(30 min, timeoutMs). */
  callTimeoutMs?: number;
}

export class StdioTransport implements McpTransport {
  private proc?: ChildProcess;
  private buf = '';
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: any) => void; reject: (e: any) => void; timer?: ReturnType<typeof setTimeout>; onProgress?: (p: McpProgress) => void; done?: () => void }>();

  constructor(private spec: StdioServerSpec) {}

  async start(): Promise<void> {
    const { command, args = [], env, cwd } = this.spec;
    // Windows: `npx`/`uvx`-style launchers are .cmd shims node can't spawn directly — resolveSpawn runs
    // them through cmd.exe (identity on POSIX). The lookup uses the merged env, so a PATH in `env` counts.
    const childEnv = { ...process.env, ...env };
    const sp = resolveSpawn(command, args, childEnv);
    const proc = spawn(sp.command, sp.args, { stdio: ['pipe', 'pipe', 'pipe'], env: childEnv, cwd, ...(IS_WIN ? { windowsHide: true, windowsVerbatimArguments: sp.windowsVerbatimArguments } : {}) });
    this.proc = proc;
    proc.stdout!.setEncoding('utf8');
    proc.stdout!.on('data', (chunk: string) => this.onData(chunk));
    proc.stderr!.setEncoding('utf8');
    proc.stderr!.on('data', (chunk: string) => log.debug(`[${command}] stderr:`, chunk.trimEnd())); // server diagnostics
    proc.on('exit', (code) => this.failAll(new Error(`MCP server "${command}" exited (code ${code})`)));
    proc.on('error', (e) => this.failAll(e instanceof Error ? e : new Error(String(e))));
  }

  private onData(chunk: string): void {
    this.buf += chunk;
    let nl: number;
    while ((nl = this.buf.indexOf('\n')) >= 0) {
      const line = this.buf.slice(0, nl).trim();
      this.buf = this.buf.slice(nl + 1);
      if (!line) continue;
      try { this.dispatch(JSON.parse(line)); }
      catch (e) { log.debug('dropping non-JSON line from MCP server:', line, e); } // never throw out of the stream handler
    }
  }

  private dispatch(msg: any): void {
    // Progress for one of our requests (token = its id) — a liveness signal for a long tool call.
    if (msg?.method === 'notifications/progress') {
      const p = this.pending.get(msg.params?.progressToken);
      try { p?.onProgress?.(msg.params ?? {}); } catch (e) { log.debug('onProgress handler threw', e); }
      return;
    }
    // Otherwise we're a minimal client: only correlate responses to our requests; ignore server→client calls.
    if (msg?.id == null || !this.pending.has(msg.id)) return;
    const p = this.pending.get(msg.id)!;
    this.pending.delete(msg.id);
    clearTimeout(p.timer); p.done?.();
    if (msg.error) p.reject(new Error(msg.error?.message ?? JSON.stringify(msg.error)));
    else p.resolve(msg.result);
  }

  private failAll(e: Error): void {
    for (const p of this.pending.values()) { clearTimeout(p.timer); p.done?.(); p.reject(e); }
    this.pending.clear();
  }

  private write(msg: unknown): void {
    if (!this.proc?.stdin) throw new Error('MCP stdio transport not started');
    this.proc.stdin.write(JSON.stringify(msg) + '\n');
  }

  request(method: string, params?: unknown, opts: McpRequestOptions = {}): Promise<any> {
    const id = this.nextId++;
    const timeoutMs = deadlineFor(method, this.spec, opts);
    const { signal, onProgress } = opts;
    return new Promise((resolve, reject) => {
      if (signal?.aborted) return reject(abortError(signal));
      const timer = timeoutMs > 0 ? setTimeout(() => {
        this.pending.delete(id); done();
        reject(new Error(`MCP request "${method}" timed out after ${timeoutMs}ms`));
      }, timeoutMs) : undefined;
      // Cancelled by the caller (a JobKill / stuck-job reap): stop waiting AND tell the server to stop working.
      const onAbort = () => {
        if (!this.pending.delete(id)) return;
        clearTimeout(timer);
        this.notify('notifications/cancelled', { requestId: id, reason: String((signal?.reason as any)?.message ?? 'cancelled') }).catch((e) => log.debug('cancel notify failed', e));
        reject(abortError(signal!));
      };
      const done = () => signal?.removeEventListener('abort', onAbort);
      signal?.addEventListener('abort', onAbort, { once: true });
      this.pending.set(id, { resolve, reject, timer, onProgress, done });
      try { this.write({ jsonrpc: '2.0', id, method, params: onProgress ? withProgressToken(params, id) : params }); }
      catch (e) { clearTimeout(timer); done(); this.pending.delete(id); reject(e); }
    });
  }

  async notify(method: string, params?: unknown): Promise<void> {
    this.write({ jsonrpc: '2.0', method, params });
  }

  async close(): Promise<void> {
    this.failAll(new Error('MCP transport closed'));
    try { this.proc?.stdin?.end(); } catch (e) { log.debug('stdin end failed', e); }
    // Windows: the child may be cmd.exe running a .cmd shim — kill the TREE, or the server it started lives on.
    if (!(IS_WIN && killTree(this.proc?.pid, 'SIGTERM'))) this.proc?.kill();
  }
}

// ---------------------------------------------------------------------------
// HTTP transport — Streamable HTTP: POST JSON-RPC, parse JSON or an SSE response.
// ---------------------------------------------------------------------------
export interface HttpServerSpec {
  url: string;
  headers?: Record<string, string>;
  /** Sugar over `headers` → `Authorization: Bearer <token>`. The headless-friendly path (no OAuth loopback);
   *  inject a static token (from env / VFS secret) per our CLAUDE.md rule preferring API keys for cron/edge. */
  bearerToken?: string;
  /** Protocol request deadline (initialize, tools/list, …). Default 30s. */
  timeoutMs?: number;
  /** `tools/call` hard cap. Default max(30 min, timeoutMs). */
  callTimeoutMs?: number;
}

/** Thrown when an HTTP MCP server rejects auth (401/403). Carries `needsAuth` so callers can surface a hint
 *  ("set bearerToken / headers") instead of a generic mount failure — without attempting an interactive flow. */
export class McpAuthError extends Error {
  needsAuth = true;
  constructor(public status: number, public serverName?: string) {
    super(`MCP server${serverName ? ` "${serverName}"` : ''} requires authentication (HTTP ${status}) — set bearerToken or headers`);
    this.name = 'McpAuthError';
  }
}

export class HttpTransport implements McpTransport {
  private nextId = 1;
  private sessionId?: string;
  private fetchImpl: typeof fetch;

  constructor(private spec: HttpServerSpec, fetchImpl?: typeof fetch) {
    this.fetchImpl = fetchImpl ?? fetch;
  }

  async start(): Promise<void> {}

  async request(method: string, params?: unknown, opts: McpRequestOptions = {}): Promise<any> {
    const id = this.nextId++;
    const deadline = AbortSignal.timeout(deadlineFor(method, this.spec, opts));
    const signal = opts.signal ? AbortSignal.any([opts.signal, deadline]) : deadline;
    try {
      return await this.post({ jsonrpc: '2.0', id, method, params: opts.onProgress ? withProgressToken(params, id) : params }, false, signal, opts.onProgress);
    } catch (e) {
      if (opts.signal?.aborted) { // caller cancelled: tell the server to stop too (best-effort, never awaited by the caller)
        void this.notify('notifications/cancelled', { requestId: id, reason: String((opts.signal.reason as any)?.message ?? 'cancelled') }).catch((err) => log.debug('cancel notify failed', err));
        throw abortError(opts.signal);
      }
      throw e;
    }
  }

  async notify(method: string, params?: unknown): Promise<void> {
    await this.post({ jsonrpc: '2.0', method, params }, true, AbortSignal.timeout(this.spec.timeoutMs ?? DEFAULT_TIMEOUT_MS));
  }

  private async post(msg: any, isNotify: boolean, signal: AbortSignal, onProgress?: (p: McpProgress) => void): Promise<any> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      accept: 'application/json, text/event-stream',
      ...(this.spec.bearerToken ? { authorization: `Bearer ${this.spec.bearerToken}` } : {}),
      ...this.spec.headers, // explicit headers win — lets a caller override the bearer sugar if needed
      ...(this.sessionId ? { 'mcp-session-id': this.sessionId } : {}),
    };
    const resp = await this.fetchImpl(this.spec.url, {
      method: 'POST', headers, body: JSON.stringify(msg), signal,
    });
    const sid = resp.headers.get('mcp-session-id');
    if (sid) this.sessionId = sid; // adopt the server-assigned session for subsequent calls
    if (resp.status === 401 || resp.status === 403) throw new McpAuthError(resp.status);
    if (!resp.ok) throw new Error(`MCP HTTP ${resp.status} ${resp.statusText}`);
    if (isNotify) return;
    const ct = resp.headers.get('content-type') ?? '';
    const data = ct.includes('text/event-stream') ? await readSseResponse(resp, msg.id, onProgress) : await resp.json();
    if (data?.error) throw new Error(data.error?.message ?? JSON.stringify(data.error));
    return data?.result;
  }

  async close(): Promise<void> {}
}

/** One SSE `data:` line → the JSON-RPC message it carries (undefined when it isn't one). */
function sseMessage(line: string): any {
  const trimmed = line.trimStart();
  if (!trimmed.startsWith('data:')) return undefined;
  try { return JSON.parse(trimmed.slice(5).trim()); }
  catch (e) { log.debug('skipping unparseable SSE data line', e); return undefined; }
}

/** Read an SSE response INCREMENTALLY: `notifications/progress` events are delivered as they arrive (liveness for a
 *  long tool call), and the first response object (result/error) ends the read. */
async function readSseResponse(resp: Response, id: unknown, onProgress?: (p: McpProgress) => void): Promise<any> {
  const handle = (obj: any): any => {
    if (obj?.method === 'notifications/progress') { try { onProgress?.(obj.params ?? {}); } catch (e) { log.debug('onProgress handler threw', e); } return undefined; }
    if (obj && (obj.result !== undefined || obj.error !== undefined) && (obj.id == null || id == null || obj.id === id)) return obj;
    return undefined;
  };
  const reader = resp.body?.getReader?.();
  if (!reader) { for (const l of (await resp.text()).split('\n')) { const r = handle(sseMessage(l)); if (r) return r; } return {}; }
  const dec = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      buf += done ? dec.decode() : dec.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n')) >= 0 || (done && buf)) {
        const line = nl >= 0 ? buf.slice(0, nl) : buf;
        buf = nl >= 0 ? buf.slice(nl + 1) : '';
        const r = handle(sseMessage(line));
        if (r) return r;
      }
      if (done) return {};
    }
  } finally { reader.cancel().catch(() => {}); }
}

// ---------------------------------------------------------------------------
// Client — handshake + discovery on top of a transport.
// ---------------------------------------------------------------------------
export class McpClient {
  constructor(public transport: McpTransport, private clientInfo = { name: 'agentx', version: '0' }) {}

  /** `initialize` handshake → `notifications/initialized`. Returns the server's init result. */
  async connect(): Promise<{ serverInfo?: { name?: string; version?: string }; capabilities?: any }> {
    await this.transport.start();
    const result = await this.transport.request('initialize', {
      protocolVersion: PROTOCOL_VERSION,
      capabilities: {},
      clientInfo: this.clientInfo,
    });
    await this.transport.notify('notifications/initialized');
    return result ?? {};
  }

  async listTools(): Promise<McpToolSpec[]> {
    const r = await this.transport.request('tools/list');
    return Array.isArray(r?.tools) ? r.tools : [];
  }

  /** `tools/call`. Pass `onProgress` to request progress notifications (liveness) and `signal` to cancel. */
  async callTool(name: string, args: unknown, opts?: McpRequestOptions): Promise<unknown> {
    return this.transport.request('tools/call', { name, arguments: args ?? {} }, opts);
  }

  async listResources(): Promise<Array<{ uri: string; name?: string; mimeType?: string }>> {
    const r = await this.transport.request('resources/list');
    return Array.isArray(r?.resources) ? r.resources : [];
  }

  async readResource(uri: string): Promise<any> {
    return this.transport.request('resources/read', { uri });
  }

  async close(): Promise<void> {
    await this.transport.close();
  }
}

// ---------------------------------------------------------------------------
// Config-driven mounting — Claude-style `mcpServers: { name: {...} }`.
// ---------------------------------------------------------------------------
/** One server: stdio (`command`+`args`+`env`) OR http (`url`+`headers`). `disabled` skips it. */
export interface McpServerConfig {
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  cwd?: string;
  url?: string;
  headers?: Record<string, string>;
  /** `Authorization: Bearer <token>` sugar for http servers (headless-friendly; no OAuth flow). */
  bearerToken?: string;
  /** Auth strategy for http servers. `'oauth'` → the CLI resolves a token via the attended OAuth flow
   *  (cli/mcpOAuth.ts) and injects it as `bearerToken` at mount; the core never runs the flow. Default none. */
  auth?: 'oauth' | 'none';
  /** Protocol request deadline (initialize, tools/list, …). Default 30s. */
  timeoutMs?: number;
  /** `tools/call` hard cap (the call is handed to a job long before; this bounds what nothing else stops). Default 30 min. */
  callTimeoutMs?: number;
  disabled?: boolean;
}

export interface MountedMcp {
  name: string;
  client: McpClient;
  tools: AgentTool[];
  /** Raw tool specs discovered at mount (already fetched internally) — exposed so deferred-mount
   *  glue (`makeMcpToolSearchFromMounted`) needn't re-call `tools/list`. */
  specs: McpToolSpec[];
  serverInfo?: { name?: string; version?: string };
  /** The config this server was mounted with — lets a host remount it (`/mcp reconnect`). */
  config: McpServerConfig;
}

/** Build the transport for a server config (http when `url`, else stdio). Shared by every mount path. */
function buildTransport(cfg: McpServerConfig): McpTransport {
  return cfg.url
    ? new HttpTransport({ url: cfg.url, headers: cfg.headers, bearerToken: cfg.bearerToken, timeoutMs: cfg.timeoutMs, callTimeoutMs: cfg.callTimeoutMs })
    : new StdioTransport({ command: cfg.command!, args: cfg.args, env: cfg.env, cwd: cfg.cwd, timeoutMs: cfg.timeoutMs, callTimeoutMs: cfg.callTimeoutMs });
}

/** Race `p` against `ms` (a per-server mount deadline). `ms` falsy/≤0 → no deadline. The caller
 *  closes the half-open client on rejection so a hung server leaks nothing. */
function withTimeout<T>(p: Promise<T>, ms: number | undefined, label: string): Promise<T> {
  if (!ms || ms <= 0) return p;
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`MCP "${label}" mount exceeded ${ms}ms`)), ms);
    (timer as any).unref?.();
    p.then((v) => { clearTimeout(timer); resolve(v); }, (e) => { clearTimeout(timer); reject(e); });
  });
}

/** Connect + discover + adapt, with optional per-server deadline; closes the client if it fails. */
async function mountWithDeadline(name: string, cfg: McpServerConfig, mountTimeoutMs?: number): Promise<MountedMcp> {
  const client = new McpClient(buildTransport(cfg));
  try {
    return await withTimeout((async () => {
      const init = await client.connect();
      const specs = await client.listTools();
      const tools = mcpToolsToAgentTools(specs, (tool, a, o) => client.callTool(tool, a, o), `mcp__${name}__`);
      return { name, client, tools, specs, serverInfo: init?.serverInfo, config: cfg } as MountedMcp;
    })(), mountTimeoutMs, name);
  } catch (e) {
    await client.close().catch((err) => log.debug(`close after failed mount of "${name}": ${err}`));
    throw e;
  }
}

/** Connect one server, discover its tools, and adapt them to prefixed AgentTools (`mcp__<name>__`). */
export function mountMcpServer(name: string, cfg: McpServerConfig): Promise<MountedMcp> {
  return mountWithDeadline(name, cfg);
}

/** Drop disabled/incomplete entries, warning on the incomplete ones. */
function validEntries(servers: Record<string, McpServerConfig>): Array<[string, McpServerConfig]> {
  return Object.entries(servers).filter(([name, cfg]) => {
    if (!cfg || cfg.disabled) return false;
    if (!cfg.command && !cfg.url) { log.warn(`MCP server "${name}" needs a command (stdio) or url (http) — skipping`); return false; }
    return true;
  });
}

/** Log a mount failure (auth vs generic) without throwing — one broken server never blocks startup. */
function logMountFailure(name: string, e: any): void {
  if (e instanceof McpAuthError) log.warn(`MCP "${name}" needs-auth: HTTP ${e.status} — set bearerToken or headers in its config; skipping`);
  else log.error(`MCP server "${name}" failed to mount: ${e?.message ?? e}`); // logged, then skipped
}

/**
 * Mount every configured server in PARALLEL (one slow/dead server no longer serializes the rest);
 * each may carry a `mountTimeoutMs` deadline. A server that fails is logged and skipped.
 */
export async function mountMcpServers(servers: Record<string, McpServerConfig> = {}, opts: { mountTimeoutMs?: number } = {}): Promise<MountedMcp[]> {
  const entries = validEntries(servers);
  const settled = await Promise.allSettled(entries.map(([name, cfg]) => mountWithDeadline(name, cfg, opts.mountTimeoutMs)));
  const out: MountedMcp[] = [];
  settled.forEach((r, i) => {
    const name = entries[i][0];
    // debug (not info): the CLI prints its own one-line mount summary — an always-on info here double-prints
    if (r.status === 'fulfilled') { out.push(r.value); log.debug(`MCP "${name}" mounted — ${r.value.tools.length} tool(s)${r.value.serverInfo?.name ? ` from ${r.value.serverInfo.name}` : ''}`); }
    else logMountFailure(name, r.reason);
  });
  return out;
}

/**
 * One-shot deferred MCP mount: connect every entry (failures dropped, never block startup) and
 * fold the survivors into a single `ToolSearch`/`McpCall` pair via `makeMcpToolSearchFromMounted`.
 * The node-only counterpart to that edge-safe helper — returns `mounted` too so callers can close
 * the clients on shutdown. Eager: it connects all servers up front. For lazy connect + a cached
 * catalog (zero connections on a turn that uses no MCP tool), use `mountMcpCatalog`.
 */
export async function mountMcpDeferred(
  servers: Record<string, McpServerConfig> = {},
  options?: McpToolSearchOptions & { mountTimeoutMs?: number },
): Promise<{ tools: AgentTool[]; serverNames: string[]; toolCount: number; mounted: MountedMcp[] }> {
  const mounted = await mountMcpServers(servers, { mountTimeoutMs: options?.mountTimeoutMs });
  return { ...makeMcpToolSearchFromMounted(mounted, options), mounted };
}

// ---------------------------------------------------------------------------
// Lazy connect + catalog cache — defer the CONNECTION, not just the schemas.
// ---------------------------------------------------------------------------
/** A persistent tool catalog: lets `mountMcpCatalog` build `ToolSearch` WITHOUT connecting when a
 *  fresh entry exists. The lib defines the interface; the consumer supplies the store (RAM/disk).
 *  Key = a hash of the server's resolved config (see `mcpConfigKey`). TTL/versioning is the store's. */
export interface McpCatalogStore {
  get(key: string): McpToolSpec[] | null;
  set(key: string, specs: McpToolSpec[]): void;
}

/** Stable cache key for a server config — command/args/cwd + env NAMES (not secret values), or
 *  url + header names. Changing any of these invalidates the cached catalog; rotating a secret does not. */
export function mcpConfigKey(cfg: McpServerConfig): string {
  const parts = cfg.url
    ? ['http', cfg.url, ...Object.keys(cfg.headers ?? {}).sort()]
    : ['stdio', cfg.command ?? '', ...(cfg.args ?? []), cfg.cwd ?? '', ...Object.keys(cfg.env ?? {}).sort()];
  return createHash('sha256').update(parts.join('\0')).digest('hex').slice(0, 16);
}

/** Default in-memory catalog: process-lifetime, hash-keyed, TTL-expiring (so a server that gains
 *  tools is picked up after the TTL without a restart). Shared module singleton → cross-turn reuse. */
export class MemMcpCatalog implements McpCatalogStore {
  private m = new Map<string, { specs: McpToolSpec[]; exp: number }>();
  constructor(private ttlMs = 5 * 60_000) {}
  get(key: string): McpToolSpec[] | null {
    const e = this.m.get(key);
    if (!e) return null;
    if (Date.now() > e.exp) { this.m.delete(key); return null; }
    return e.specs;
  }
  set(key: string, specs: McpToolSpec[]): void { this.m.set(key, { specs, exp: Date.now() + this.ttlMs }); }
}

/** Opt-in warm pool: keeps stdio MCP clients connected across turns, reaping each after `ttlMs`
 *  idle. Most stdio MCPs are per-turn by design, so this is off by default; HTTP servers are
 *  stateless and never pooled. */
export class McpPool {
  private warm = new Map<string, { client: McpClient; timer: ReturnType<typeof setTimeout> }>();
  constructor(private ttlMs = 5 * 60_000) {}
  get(key: string): McpClient | null { const e = this.warm.get(key); if (!e) return null; this.arm(key, e); return e.client; }
  put(key: string, client: McpClient): void {
    const prev = this.warm.get(key); // overwrite: cancel the stale timer + close the displaced client
    if (prev) { clearTimeout(prev.timer); if (prev.client !== client) void prev.client.close().catch((err) => log.debug(`warm-pool replace close failed: ${err}`)); }
    const e = { client, timer: undefined as any };
    this.warm.set(key, e); this.arm(key, e);
  }
  private arm(key: string, e: { client: McpClient; timer: any }): void {
    clearTimeout(e.timer);
    e.timer = setTimeout(() => { void this.evict(key); }, this.ttlMs);
    (e.timer as any).unref?.();
  }
  private async evict(key: string): Promise<void> {
    const e = this.warm.get(key); if (!e) return;
    this.warm.delete(key);
    await e.client.close().catch((err) => log.debug(`warm-pool evict close failed: ${err}`));
  }
  async closeAll(): Promise<void> { for (const e of this.warm.values()) { clearTimeout(e.timer); await e.client.close().catch(() => {}); } this.warm.clear(); }
}

const defaultCatalog = new MemMcpCatalog();
const defaultPool = new McpPool();
const defaultFailures = new Map<string, number>(); // configKey → cooldown-until epoch ms (negative cache)
const bgInflight = new Set<string>();              // configKey → background discovery in flight (dedupe)
const DEFAULT_FAILURE_COOLDOWN_MS = 60_000;

export interface McpCatalogOptions extends McpToolSearchOptions {
  /** Where to read/write discovered specs. Default: a shared process-lifetime `MemMcpCatalog`. */
  catalog?: McpCatalogStore;
  /** Per-server deadline for the connect+list on a cache MISS. A hung server is skipped, not blocking. */
  mountTimeoutMs?: number;
  /** Opt-in: keep stdio clients warm across turns (see `McpPool`). HTTP servers are never pooled. */
  keepWarm?: boolean;
  /** Warm-client pool. Default: a shared process-lifetime `McpPool` (only used when `keepWarm`). */
  pool?: McpPool;
  /** Negative cache: after a server fails/times out discovery, skip it for this long so it never
   *  re-floors a turn at the deadline. Default 60s; the server is re-probed once the cooldown lapses. */
  failureCooldownMs?: number;
  /** Shared failure-cooldown map (configKey → until-epoch-ms). Default: a process-lifetime singleton. */
  failures?: Map<string, number>;
  /** Discovery policy on a cache MISS:
   *  - `'connect'` (default): synchronously connect+list (deadline-bounded) — blocks until ready.
   *  - `'cache-only'`: NEVER block the turn — serve only cached servers, and kick uncached discovery
   *    to the background (so it's warm next turn). Pair with a boot/timer `warmMcpCatalog` so the
   *    first turn is already a cache hit. */
  discover?: 'connect' | 'cache-only';
}

interface Discovered { name: string; cfg: McpServerConfig; key: string; specs: McpToolSpec[] }

/** Connect+list one server (deadline-bounded), populate the catalog on success / negative-cache on
 *  failure. Closes the client unless `keepWarm` keeps it for the lazy-call phase. */
async function connectAndList(name: string, cfg: McpServerConfig, key: string, opts: McpCatalogOptions): Promise<Discovered | null> {
  const catalog = opts.catalog ?? defaultCatalog;
  const pool = opts.pool ?? defaultPool;
  const failures = opts.failures ?? defaultFailures;
  const client = new McpClient(buildTransport(cfg));
  try {
    const specs = await withTimeout((async () => { await client.connect(); return client.listTools(); })(), opts.mountTimeoutMs, name);
    catalog.set(key, specs);
    failures.delete(key); // recovered
    if (opts.keepWarm && !cfg.url) pool.put(key, client); // reuse this connection for the lazy-call phase
    else await client.close().catch(() => {});
    return { name, cfg, key, specs };
  } catch (e: any) {
    await client.close().catch(() => {});
    failures.set(key, Date.now() + (opts.failureCooldownMs ?? DEFAULT_FAILURE_COOLDOWN_MS));
    logMountFailure(name, e);
    return null;
  }
}

/** Resolve one server to its specs: cache hit → no connection; in failure-cooldown → skip;
 *  otherwise connect (or, in `cache-only` mode, kick background discovery and skip for now). */
async function discoverOne(name: string, cfg: McpServerConfig, opts: McpCatalogOptions): Promise<Discovered | null> {
  const catalog = opts.catalog ?? defaultCatalog;
  const failures = opts.failures ?? defaultFailures;
  const key = mcpConfigKey(cfg);
  const cached = catalog.get(key);
  if (cached) return { name, cfg, key, specs: cached };
  const until = failures.get(key);
  if (until && until > Date.now()) return null; // negative cache → known-down, skip
  if (opts.discover === 'cache-only') {
    if (!bgInflight.has(key)) { bgInflight.add(key); void connectAndList(name, cfg, key, opts).finally(() => bgInflight.delete(key)); }
    return null; // not available THIS turn; warms for the next one
  }
  return connectAndList(name, cfg, key, opts);
}

/**
 * Lazy + cached MCP mount. Builds the `ToolSearch`/`McpCall` pair from the CACHED catalog when one
 * exists — connecting NOTHING. On a cache miss it (by default) connects once (parallel, deadline-
 * bounded), lists, caches, then disconnects (or keeps warm if `keepWarm`). A server is connected only
 * when one of its tools is actually invoked via `McpCall` (memoized per turn; reused from the warm
 * pool if enabled). A server that fails/times out is negative-cached (`failureCooldownMs`) so it
 * never re-floors a later turn.
 *
 * For zero turn-path latency even on a cold process, set `discover: 'cache-only'` and call
 * `warmMcpCatalog` at boot + on a timer: the turn then NEVER synchronously connects — it serves
 * cached servers and warms the rest in the background.
 *
 * Per-turn cost: a turn using NO MCP tool → 0 connections; a turn using one → exactly one.
 */
export async function mountMcpCatalog(
  servers: Record<string, McpServerConfig> = {},
  opts: McpCatalogOptions = {},
): Promise<{ tools: AgentTool[]; serverNames: string[]; toolCount: number; connect(name: string): Promise<McpClient>; close(): Promise<void> }> {
  const pool = opts.pool ?? defaultPool;
  const discovered = (await Promise.all(validEntries(servers).map(([name, cfg]) => discoverOne(name, cfg, opts)))).filter(Boolean) as Discovered[];

  const byName = new Map(discovered.map((d) => [d.name, d]));
  const inflight = new Map<string, Promise<McpClient>>(); // memoize per server → concurrent calls share one connect
  const live: McpClient[] = []; // non-warm clients opened this turn (closed on close())

  const connect = (name: string): Promise<McpClient> => {
    let p = inflight.get(name);
    if (p) return p;
    p = (async () => {
      const d = byName.get(name);
      if (!d) throw new Error(`MCP server '${name}' is not in the catalog`);
      const warmable = opts.keepWarm && !d.cfg.url;
      if (warmable) { const w = pool.get(d.key); if (w) return w; }
      const client = new McpClient(buildTransport(d.cfg));
      await client.connect();
      if (warmable) pool.put(d.key, client); else live.push(client);
      return client;
    })();
    inflight.set(name, p);
    return p;
  };

  const search = makeLazyMcpToolSearch(
    discovered.map((d) => ({ name: d.name, specs: d.specs })),
    async (server, rawName, args, o) => (await connect(server)).callTool(rawName, args, o),
    opts,
  );
  const close = async (): Promise<void> => { for (const c of live) await c.close().catch(() => {}); live.length = 0; inflight.clear(); };
  return { ...search, connect, close };
}

/**
 * Off-turn catalog warm-up: do the one cold discovery pass (connect + list, parallel, deadline-
 * bounded) so a later `mountMcpCatalog(..., { discover: 'cache-only' })` is a cache HIT. Call at
 * server boot and on a timer (cadence < the catalog TTL). Cache hits cost nothing; down servers are
 * negative-cached so turns never touch them. Returns which servers warmed vs failed.
 */
export async function warmMcpCatalog(
  servers: Record<string, McpServerConfig> = {},
  opts: McpCatalogOptions = {},
): Promise<{ warmed: string[]; failed: string[]; toolCount: number }> {
  const entries = validEntries(servers);
  const discovered = (await Promise.all(entries.map(([name, cfg]) => discoverOne(name, cfg, { ...opts, discover: 'connect' })))).filter(Boolean) as Discovered[];
  const warmedNames = new Set(discovered.map((d) => d.name));
  return {
    warmed: discovered.map((d) => d.name),
    failed: entries.map(([n]) => n).filter((n) => !warmedNames.has(n)),
    toolCount: discovered.reduce((s, d) => s + d.specs.length, 0),
  };
}
